/**
 * 64.6 step 1 (ARCHITECTURE §5.4 "64.6, the design"): the clock policy a match runs under, chosen by its setup — not a
 * branch on `config.clock` inside the tick. Two today:
 * - **continuous** (the action clock): every unit acts every tick; the input window never closes; the step counts its
 *   own tick, mid-step (what is decided at tick T is stamped T);
 * - **turn** (classic): a turn is a commitment pause; a tick plays out what a commit set going — a bomblet's countdown, a
 *   round's playback, a flight — and the tick count rises after it.
 * The airborne step under both. Step 2 gives the turn policy its window (the turn loop moves into the Match from the
 * hosts); this step only names the two and routes the tick through them, so nothing moves.
 */

import type { GameConfig } from '../types';

export type ClockPolicyKind = 'turn' | 'continuous';

export interface ClockPolicy {
  readonly kind: ClockPolicyKind;
  /** The step counts its own tick, mid-step (else the tick count rises after the step). */
  readonly ownTick: boolean;
  /** Inputs are taken every tick: the commit window never closes. */
  readonly windowAlwaysOpen: boolean;
  /**
   * 63.4 step 7: how a blast reaches what stands — `shape`: by the distance to its shape (core/hits.ts; a structure over
   * the blast's damage circle, a prop within the blast's own radius through the damage door, a rocket's burst covered by
   * what lies between, a gunner whose gun it destroys under decision 124's hatch rule); `centre`: the turn game's rules
   * (a structure from its centre, structureBlastReach; the flora rule) — kept because moving them re-records every
   * headless case for nothing a player of L0 or L1 would see.
   */
  readonly blastReach: 'shape' | 'centre';
  /**
   * Decision 134 (1): a shell's crater at person scale — `capped`: no deeper than CRATER_DEPTH_CAP_PERSONS person
   * heights below the generated ground (core/terrainEdits.ts craterDepthCap); `planet`: the planet family's whole depth
   * (SCALE.md rule 1) — the turn game, whose human profile replays the classic golden exactly (S2h).
   */
  readonly craterDepth: 'capped' | 'planet';
  /**
   * 63.7 (b), decision 135 B: how the planet-scale buildings (the team's building — the ecotourism centre, the shield, the
   * landing pad — and the antenna) are sized — `function`: at a person scale by what each is for (BUILDING_PERSON_SCALE:
   * doors and floors at the reference, the same on every planet; core/buildingScale.ts), at classic scale planet-scaled as
   * ever (people and buildings share the planet's family there); `planet`: planet-scaled (surfacePropScale) — the turn
   * game, whose human profile replays the classic golden exactly (S2h).
   */
  readonly buildings: 'function' | 'planet';
  /**
   * 63.5 step 6, decision 155 (the owner: "shift things over to all use ground material system"): what the ground is made
   * of — `materials`: every reader asks the ground's material (core/groundMaterial.ts): a crater digs and looks by it,
   * with its decals and its foxhole's depth, and a walker's pace and footing follow it (core/groundWalk.ts); `plain`: the
   * turn game's one undifferentiated ground, kept because giving it materials re-records every headless case for
   * nothing a player of L0 or L1 would see. There is no switch: the clock decides.
   */
  readonly ground: 'materials' | 'plain';
}

export const TURN_POLICY: ClockPolicy = Object.freeze({ kind: 'turn', ownTick: false, windowAlwaysOpen: false, blastReach: 'centre', craterDepth: 'planet', buildings: 'planet', ground: 'plain' });
export const CONTINUOUS_POLICY: ClockPolicy = Object.freeze({ kind: 'continuous', ownTick: true, windowAlwaysOpen: true, blastReach: 'shape', craterDepth: 'capped', buildings: 'function', ground: 'materials' });

/**
 * 64.6 step 6: the tick reads the policy's data, never its kind — `windowAlwaysOpen` says whether every unit acts every
 * tick and whether a commit window's rules (a settled shot, the window owed after a round) apply; `ownTick` when the
 * tick counts; `blastReach` how a blast reaches a body (lane F). `kind` names the policy for hosts and tests.
 */
/** The policy a match's config selects (today from `clock`; a setup naming the policy directly is step 2's). */
export function clockPolicyFor(config: Pick<GameConfig, 'clock'>): ClockPolicy {
  return config.clock === 'action' ? CONTINUOUS_POLICY : TURN_POLICY;
}
